import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  Autocomplete,
  alpha,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import Disclaimer from '@/components/common/Disclaimer';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { usePageTitle } from '@/hooks/usePageTitle';

// Curated list of popular bloggers
const POPULAR_BLOGGERS = [
  '老李玩钱',
  'David（股票博主David——美股解读）',
  '阳光财经',
  '视野环球财经',
  'XTrader猫姐美股',
  '美股咖啡館',
  'NaNa说美股',
];

export default function BloggerTrackingPage() {
  usePageTitle('博主追踪');

  const navigate = useNavigate();
  const { data: filterOptions } = useFilterOptions();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');

  const handleChannelSelect = (channel: string) => {
    navigate(`/channel/${encodeURIComponent(channel)}`);
  };

  const handleSearchChange = (_: React.SyntheticEvent, value: string | null) => {
    if (value) {
      handleChannelSelect(value);
      setSearchValue(null);
      setInputValue('');
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      handleChannelSelect(inputValue.trim());
      setSearchValue(null);
      setInputValue('');
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
          博主追踪
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          追踪特定博主的历史观点，了解其投资风格和观点变化
        </Typography>
        <Disclaimer />

        {/* Search Box */}
        <Autocomplete
          freeSolo
          options={filterOptions?.channels || []}
          value={searchValue}
          onChange={handleSearchChange}
          inputValue={inputValue}
          onInputChange={(_, newValue) => setInputValue(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="输入博主名称搜索..."
              variant="outlined"
              onKeyDown={handleInputKeyDown}
              sx={{
                maxWidth: 500,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          )}
          sx={{ mb: 4 }}
        />
      </Box>

      {/* Popular Bloggers Grid */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          热门博主
        </Typography>
        <Grid container spacing={2}>
          {POPULAR_BLOGGERS.map((channel) => (
            <Grid key={channel} xs={6} sm={4} md={3} lg={2}>
              <Card
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea onClick={() => handleChannelSelect(channel)}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      py: 2.5,
                    }}
                  >
                    <PersonIcon
                      sx={{
                        fontSize: 20,
                        color: alpha('#1976d2', 0.7),
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '120px',
                      }}
                    >
                      {channel}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Feature Description */}
      <Box sx={{ mt: 5, p: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          功能说明
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • 查看特定博主在不同时间段内的所有观点
          <br />
          • 支持按时间范围筛选：最近一周、一月、三月或全部
          <br />
          • 支持按标的筛选，只看特定 ticker 的观点
          <br />
          • 需要登录后才能查看详细观点内容
        </Typography>
      </Box>
    </Box>
  );
}
